import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { API_BASE, getHealth, type HealthInfo } from '@/lib/api'

interface State {
  loading: boolean
  info?: HealthInfo
  error?: string
}

const ENDPOINTS = [
  ['GET /api/health', '服务与配置探活'],
  ['POST /api/chat', '一次性返回完整回答'],
  ['POST /api/chat/stream', 'SSE 流式回答（界面当前使用）'],
  ['GET /api/conversations/:id', '按会话读取历史轮次'],
]

function Row({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line/70 py-2.5 last:border-0">
      <span className="text-xs text-muted">{label}</span>
      <span className="truncate text-sm text-ink">{value}</span>
    </div>
  )
}

export function StatusPage() {
  const [state, setState] = useState<State>({ loading: true })

  const check = useCallback(() => {
    getHealth()
      .then((info) => setState({ loading: false, info }))
      .catch((e: Error) => setState({ loading: false, error: e.message }))
  }, [])

  useEffect(check, [check])

  const online = state.info?.status === 'ok'

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="后端服务" subtitle={API_BASE} actions={<Button size="sm" onClick={check}>重新检测</Button>}>
        <div className="px-4 py-2">
          <Row
            label="连接状态"
            value={
              state.loading ? (
                '检测中…'
              ) : online ? (
                <Badge tone="ok">在线</Badge>
              ) : (
                <Badge tone="danger">不可用</Badge>
              )
            }
          />
          <Row label="模型" value={state.info?.model ?? '—'} />
          <Row
            label="会话存储"
            value={state.info?.memory === 'kv' ? 'Cloudflare KV' : state.info ? '未绑定（仅本次请求）' : '—'}
          />
          <Row label="错误" value={state.error ? <span className="text-danger">{state.error}</span> : '无'} />
        </div>
      </Card>

      <Card title="接口契约" subtitle="前端仅依赖这层约定，后端查询逻辑待接入">
        <div className="px-4 py-2">
          {ENDPOINTS.map(([name, desc]) => (
            <Row key={name} label={<code className="text-xs text-brand-700">{name}</code>} value={desc} />
          ))}
        </div>
      </Card>
    </div>
  )
}
